import React, { useState, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  SquaresFour,
  TreeStructure,
  FileText,
  UsersThree,
  PaperPlaneTilt,
  ChartBar,
  GearSix,
  X as PhosphorX,
  SignOut,
  Lightning,
  CaretUpDown,
  // Moon as MoonIcon, Sun as SunIcon, Monitor as MonitorIcon, // used by hidden header theme toggle
  Bell,
  BellSimple,
  Tray,
  CreditCard,
  ClipboardText,
  Browsers,
  SidebarSimple,
  List,
  Warning,
  ChatText,
  Envelope,
  Sparkle,
  Compass,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { useAuthStore } from '@/stores/authStore'
import { usePermission } from '@/hooks/usePermission'
import { useThemeStore } from '@/stores/themeStore'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useUnsavedChangesStore } from '@/stores/unsavedChangesStore'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import UnsavedChangesPrompt from '@/components/ui/UnsavedChangesPrompt'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { BrandWordmark } from '@/components/ui/Logomark'
import UpgradeCard from '@/components/UpgradeCard'
import PlanLapsedBanner from '@/components/PlanLapsedBanner'
import ProductTour from '@/components/tour/ProductTour'
import { useTourStore } from '@/components/tour/tourStore'
import { tourKeyForGroup } from '@/components/tour/tourSteps'
import { cn, getInitials } from '@/lib/utils'
import { openEventStream } from '@/lib/sse'
import { formatDistanceToNow } from 'date-fns'

interface DashboardLayoutProps {
  /** Optional. Omit it and the layout renders the matched child route via
   *  <Outlet />; the prop is kept so any direct-children usage still works. */
  children?: React.ReactNode
}

// Notification icon per backend `type` (workflow_alert, reply, open, enrichment,
// topup, info, …). Color lives only inside the chip (design-aesthetics §38);
// title/body text stays neutral. Falls back to the bell for unknown types.
const NOTIF_ICONS: Record<string, PhosphorIcon> = {
  workflow_alert: Lightning,
  alert: Warning,
  approval: ClipboardText,
  reply: ChatText,
  open: Envelope,
  click: Envelope,
  send: PaperPlaneTilt,
  enrichment: Sparkle,
  contact_created: Sparkle,
  topup: CreditCard,
  card: CreditCard,
  billing: CreditCard,
  info: Bell,
}
const notifIcon = (type: string): PhosphorIcon => NOTIF_ICONS[type] ?? Bell

const editorialFontStyle: React.CSSProperties = {
  fontFamily: "'Inter', -apple-system, system-ui, 'Segoe UI', Roboto, sans-serif",
  fontFeatureSettings: '"cv01", "ss03"',
}

// OS detection for the Settings keyboard shortcut (mac = ⇧⌘, ; others = Ctrl⇧,).
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPod|iPad/i.test((navigator as any).platform || navigator.userAgent || '')

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd
    className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded border border-[rgba(15,16,17,0.12)] dark:border-white/[0.12] bg-[rgba(15,16,17,0.03)] dark:bg-white/[0.05] text-[#5b6068] dark:text-[#8a8f98]"
    style={{ fontSize: '10px', fontWeight: 590, lineHeight: 1, fontFamily: 'inherit' }}
  >
    {children}
  </kbd>
)

const SettingsShortcut: React.FC = () => (
  <span className="ml-auto flex items-center gap-0.5 pl-2">
    {IS_MAC ? (
      <>
        <Kbd>⇧</Kbd>
        <Kbd>⌘</Kbd>
        <Kbd>,</Kbd>
      </>
    ) : (
      <>
        <Kbd>Ctrl</Kbd>
        <Kbd>⇧</Kbd>
        <Kbd>,</Kbd>
      </>
    )}
  </span>
)

type NavItem = { name: string; href: string; icon: PhosphorIcon; anim: string }
type NavGroup = { label?: string; items: NavItem[] }

// Each nav icon gets a distinct hover micro-animation (keyframes in index.css),
// triggered via the Link's `group` on hover. Honors prefers-reduced-motion.
const navGroups: NavGroup[] = [
  {
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: SquaresFour, anim: 'nav-pop' },
    ]
  },
  {
    label: 'Build',
    items: [
      { name: 'Workflows', href: '/workflows', icon: TreeStructure, anim: 'nav-wiggle' },
      { name: 'Materials', href: '/materials', icon: FileText, anim: 'nav-flip' },
      { name: 'Templates', href: '/templates', icon: Browsers, anim: 'nav-slide' },
    ]
  },
  {
    label: 'Outreach',
    items: [
      { name: 'Contacts', href: '/contacts', icon: UsersThree, anim: 'nav-bob' },
      { name: 'Campaigns', href: '/campaigns', icon: PaperPlaneTilt, anim: 'nav-fly' },
      { name: 'Inbox', href: '/inbox', icon: Tray, anim: 'nav-bob' },
      { name: 'Tasks', href: '/tasks', icon: ClipboardText, anim: 'nav-wiggle' },
    ]
  },
  {
    label: 'Analyze',
    items: [
      { name: 'Analytics', href: '/analytics', icon: ChartBar, anim: 'nav-rise' },
    ]
  },
  {
    label: 'Account',
    items: [
      { name: 'Billing', href: '/billing', icon: CreditCard, anim: 'nav-slide' },
    ]
  },
]

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  // const [themeMenuOpen, setThemeMenuOpen] = useState(false) // used by the hidden header theme toggle
  const [notifMenuOpen, setNotifMenuOpen] = useState(false)
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const userTriggerRef = useRef<HTMLButtonElement>(null)
  const notifMenuRef = useRef<HTMLDivElement>(null)
  const notifTriggerRef = useRef<HTMLButtonElement>(null)

  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  // billing.* cells exist only for Owner (manage) and Admin (view) — plan,
  // payment and upgrade prompts stay hidden for everyone else, matching the
  // server-side gates on /api/billing (UX-only; the API is the enforcement
  // point).
  const canViewBilling = usePermission('billing', 'manage_subscription').allowed
  // The page ALSO has a reporting half (credit spend + history) behind
  // credit_budgets.view_usage. Account-wide holders of that cell (Manager,
  // Analyst) get the nav item too, and BillingPage renders the read-only
  // Usage/History tabs for them. A Rep's "own" scope is not a reporting
  // surface — nothing changes for them.
  const creditUsagePerm = usePermission('credit_budgets', 'view_usage')
  const canViewCreditUsage = creditUsagePerm.allowed && creditUsagePerm.scope !== 'own'
  const visibleNavGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => item.href !== '/billing' || canViewBilling || canViewCreditUsage,
      ),
    }))
    .filter((group) => group.items.length > 0)
  // theme + setTheme only used by the (currently hidden) header theme toggle
  const { /* theme, setTheme, */ actualTheme } = useThemeStore()
  const { collapsed, toggleCollapse } = useSidebarStore()
  const isDirty = useUnsavedChangesStore((s) => s.isDirty)
  const requestNavigation = useUnsavedChangesStore((s) => s.requestNavigation)
  const { notifications, unreadCount, fetchNotifications, applySnapshot, markRead, markAllRead } =
    useNotificationStore()

  // Live bell: one SSE stream pushes the list only when a notification
  // actually changes (created / read / deleted) — no fixed-interval polling.
  // If streaming is unavailable (Redis off → 503, or this session can't hold
  // a stream — see lib/sse.ts), fall back to the old 30s poll.
  useEffect(() => {
    let pollId: number | undefined
    const closeStream = openEventStream('/notifications/stream', {
      reconnectOnClose: true,
      onMessage: (data) => applySnapshot(data),
      onError: () => {
        fetchNotifications()
        pollId = window.setInterval(fetchNotifications, 30000)
      },
    })
    return () => {
      closeStream()
      if (pollId !== undefined) window.clearInterval(pollId)
    }
  }, [fetchNotifications, applySnapshot])

  // Refresh immediately when the dropdown is opened so the list is current.
  useEffect(() => {
    if (notifMenuOpen) fetchNotifications()
  }, [notifMenuOpen, fetchNotifications])

  const handleNotificationClick = (n: typeof notifications[number]) => {
    if (!n.is_read) markRead(n.id)
    setNotifMenuOpen(false)
    if (n.link) navigate(n.link)
  }

  const openLogoutConfirm = () => {
    setUserMenuOpen(false)
    setLogoutConfirmOpen(true)
  }

  // Close the account menu on outside click.
  useEffect(() => {
    if (!userMenuOpen) return
    const onDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [userMenuOpen])

  // Close the notification dropdown on outside click. This replaces the old
  // `fixed inset-0 z-10` click catcher, which sat *below* the z-30 header and
  // the z-50 menus — so clicking any other header control never dismissed it.
  useEffect(() => {
    if (!notifMenuOpen) return
    const onDown = (e: MouseEvent) => {
      if (notifMenuRef.current && !notifMenuRef.current.contains(e.target as Node)) {
        setNotifMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [notifMenuOpen])

  // Escape dismisses whichever popover is open and returns focus to its trigger.
  useEffect(() => {
    if (!userMenuOpen && !notifMenuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (notifMenuOpen) {
        setNotifMenuOpen(false)
        notifTriggerRef.current?.focus()
      }
      if (userMenuOpen) {
        setUserMenuOpen(false)
        userTriggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [userMenuOpen, notifMenuOpen])

  // Keyboard shortcut to open Settings — ⇧⌘, on mac, Ctrl⇧, elsewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = IS_MAC ? e.metaKey : e.ctrlKey
      if (mod && e.shiftKey && (e.key === ',' || e.code === 'Comma')) {
        e.preventDefault()
        navigate('/settings')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  const confirmLogout = () => {
    setLogoutConfirmOpen(false)
    logout()
    navigate('/login')
  }

  // Used by the (currently hidden) header theme toggle; uncomment to restore
  // const themeOptions = [
  //   { value: 'light', label: 'Light', icon: SunIcon },
  //   { value: 'dark', label: 'Dark', icon: MoonIcon },
  //   { value: 'system', label: 'System', icon: MonitorIcon },
  // ]

  // Close any open header popovers on navigation. The layout no longer
  // remounts between routes, so this is the only thing that resets them.
  useEffect(() => {
    setUserMenuOpen(false)
    setNotifMenuOpen(false)
    setSidebarOpen(false)
  }, [location.pathname])

  const isSettingsActive = location.pathname === '/settings'
  const isDashboardRoute = location.pathname === '/dashboard'

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" aria-hidden="true" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 transform transition-[width,transform] duration-300 will-change-[width]",
          collapsed ? "w-[64px]" : "w-[240px]",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        style={{ ...editorialFontStyle, transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <div className="flex h-full flex-col bg-white border-r border-[rgba(15,16,17,0.08)] dark:bg-[#0f1011] dark:border-white/[0.05]">
          {/* Logo + collapse toggle */}
          <div className={cn(
            "flex h-[56px] items-center overflow-hidden",
            collapsed ? "justify-center px-2" : "justify-between px-4"
          )}>
            {collapsed ? (
              <motion.button
                onClick={toggleCollapse}
                aria-label="Expand sidebar"
                className="hidden lg:flex h-8 w-8 rounded-md items-center justify-center text-[#5b6068] hover:text-[#0f1011] hover:bg-[rgba(15,16,17,0.04)] dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
                whileTap={{ scale: 0.94 }}
              >
                <SidebarSimple className="h-[18px] w-[18px] rotate-180" weight="regular" />
              </motion.button>
            ) : (
              <>
                <Link
                  to="/dashboard"
                  aria-label="Go to dashboard"
                  className="flex items-center gap-2.5 min-w-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[#7170ff]/55 transition-opacity duration-150 hover:opacity-90"
                >
                  <BrandWordmark height={24} alt="" />
                </Link>
                <div className="flex items-center gap-1 shrink-0">
                  <motion.button
                    onClick={toggleCollapse}
                    aria-label="Collapse sidebar"
                    className="hidden lg:flex h-8 w-8 rounded-md items-center justify-center text-[#8a8f98] hover:text-[#0f1011] hover:bg-[rgba(15,16,17,0.04)] dark:text-[#62666d] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
                    whileTap={{ scale: 0.94 }}
                  >
                    <SidebarSimple className="h-[18px] w-[18px]" weight="regular" />
                  </motion.button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden text-[#5b6068] hover:text-[#0f1011] hover:bg-[rgba(15,16,17,0.04)] dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
                    onClick={() => setSidebarOpen(false)}
                    aria-label="Close sidebar"
                  >
                    <PhosphorX className="h-4 w-4" weight="regular" />
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto overflow-x-hidden" aria-label="Main navigation">
            {visibleNavGroups.map((group, groupIndex) => (
              <div key={groupIndex} data-tour={tourKeyForGroup(group.label)}>
                {group.label && !collapsed && (
                  <p
                    className="px-3 pt-5 pb-2 text-[#8a8f98] dark:text-[#62666d]"
                    style={{
                      fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                      fontSize: '10px',
                      fontWeight: 400,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {group.label}
                  </p>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={(e) => {
                          if (isDirty) {
                            e.preventDefault()
                            requestNavigation(() => navigate(item.href))
                          }
                          setSidebarOpen(false)
                        }}
                        title={collapsed ? item.name : undefined}
                        aria-current={isActive ? 'page' : undefined}
                        className={cn(
                          'group flex items-center rounded-md transition-colors duration-150 whitespace-nowrap overflow-hidden px-2.5 py-[7px]',
                          isActive
                            ? 'bg-[rgba(15,16,17,0.05)] text-[#0f1011] dark:bg-white/[0.04] dark:text-[#f7f8f8]'
                            : 'text-[#5b6068] hover:text-[#0f1011] hover:bg-[rgba(15,16,17,0.03)] dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.02]'
                        )}
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        <item.icon
                          className={cn(
                            'h-4 w-4 flex-shrink-0 mr-2.5',
                            item.anim,
                            isActive
                              ? 'text-[#5e6ad2] dark:text-[#7170ff]'
                              : 'text-[#8a8f98] group-hover:text-[#0f1011] dark:text-[#62666d] dark:group-hover:text-[#d0d6e0]'
                          )}
                          weight="regular"
                        />
                        <span className="truncate">{item.name}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Tier-aware upgrade prompt (hidden when collapsed, on top tier,
              or for roles without billing access) */}
          {!collapsed && canViewBilling && <UpgradeCard />}

          {/* User Menu + inline Settings */}
          <div className="border-t border-[rgba(15,16,17,0.06)] dark:border-white/[0.05] p-2.5">
            <div className="relative" ref={userMenuRef}>
              {/* Account trigger and Settings gear are SIBLINGS. They used to be
                  nested — a <button> inside a role="button" div — which is invalid
                  HTML/ARIA and announced incoherently. Native <button> also gives
                  us Enter/Space for free, so the manual onKeyDown is gone. */}
              <div
                className={cn(
                  "flex items-center gap-1 overflow-hidden whitespace-nowrap",
                  collapsed ? "justify-center" : "pr-1.5"
                )}
              >
                <button
                  type="button"
                  ref={userTriggerRef}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label="Account menu"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                  className={cn(
                    "flex items-center gap-2.5 p-1.5 rounded-md hover:bg-[rgba(15,16,17,0.03)] dark:hover:bg-white/[0.03] transition-colors duration-150 overflow-hidden whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-[#7170ff]/55",
                    collapsed ? "justify-center" : "flex-1 min-w-0"
                  )}
                >
                  <div
                    className="w-7 h-7 rounded-full bg-[#e8eaee] text-[#3e3e44] dark:bg-[#1a1b1e] dark:text-[#d0d6e0] flex items-center justify-center shrink-0"
                    style={{
                      fontSize: '11px',
                      fontWeight: 510,
                      letterSpacing: '-0.182px',
                      boxShadow: actualTheme === 'dark'
                        ? '0 0 0 2px #0f1011, inset 0 0 0 1px rgba(255,255,255,0.08)'
                        : '0 0 0 2px #ffffff, inset 0 0 0 1px rgba(15,16,17,0.06)',
                    }}
                  >
                    {getInitials(user?.full_name || 'User')}
                  </div>

                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left min-w-0">
                        <span
                          className="block text-[#0f1011] dark:text-[#f7f8f8] truncate"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {user?.full_name}
                        </span>
                        <span
                          className="block text-[#8a8f98] dark:text-[#62666d] truncate mt-0.5"
                          style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '-0.13px' }}
                        >
                          {user?.email}
                        </span>
                      </span>

                      {/* Caret — visual affordance for the account menu */}
                      <CaretUpDown className="h-4 w-4 shrink-0 text-[#8a8f98] dark:text-[#62666d]" weight="bold" />
                    </>
                  )}
                </button>

                {/* Settings gear — a sibling control, so no stopPropagation needed */}
                {!collapsed && (
                  <button
                    type="button"
                    onClick={() => { setSidebarOpen(false); navigate('/settings') }}
                    aria-label="Settings"
                    aria-current={isSettingsActive ? 'page' : undefined}
                    className={cn(
                      'group flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[#7170ff]/55',
                      isSettingsActive
                        ? 'bg-[rgba(15,16,17,0.05)] dark:bg-white/[0.04]'
                        : 'hover:bg-[rgba(15,16,17,0.06)] dark:hover:bg-white/[0.06]'
                    )}
                  >
                    <GearSix
                      className={cn(
                        'h-4 w-4',
                        isSettingsActive
                          ? 'text-[#5e6ad2] dark:text-[#7170ff]'
                          : 'text-[#8a8f98] group-hover:text-[#0f1011] dark:text-[#62666d] dark:group-hover:text-[#d0d6e0]'
                      )}
                      weight="regular"
                    />
                  </button>
                )}
              </div>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.98 }}
                    transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className={cn(
                      "absolute bottom-full mb-1.5 bg-white border border-[rgba(15,16,17,0.10)] dark:bg-[#191a1b] dark:border-white/[0.08] rounded-lg p-1",
                      collapsed ? "left-full ml-2 min-w-[160px]" : "left-0 right-0"
                    )}
                    style={{
                      boxShadow: actualTheme === 'dark'
                        ? '0 8px 24px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(255,255,255,0.02)'
                        : '0 8px 24px rgba(15,16,17,0.08), 0 2px 6px rgba(15,16,17,0.06)',
                    }}
                  >
                    {/* Settings (+ keyboard shortcut) */}
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/settings') }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[#3e3e44] hover:text-[#0f1011] hover:bg-[rgba(15,16,17,0.04)] dark:text-[#d0d6e0] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <GearSix className="h-4 w-4 text-[#8a8f98] dark:text-[#62666d]" weight="regular" />
                      <span>Settings</span>
                      <SettingsShortcut />
                    </button>

                    {canViewBilling && (
                      <>
                        <div className="my-1 h-px bg-[rgba(15,16,17,0.06)] dark:bg-white/[0.06]" />

                        {/* Upgrade → Billing › Plans */}
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate('/billing', { state: { scrollTo: 'plans' } }) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[#5e6ad2] dark:text-[#a5b4fc] hover:bg-[#5e6ad2]/[0.08] dark:hover:bg-[#5e6ad2]/[0.12] transition-colors duration-150"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          <Lightning className="h-4 w-4" weight="fill" />
                          <span>Upgrade</span>
                        </button>
                      </>
                    )}

                    <div className="my-1 h-px bg-[rgba(15,16,17,0.06)] dark:bg-white/[0.06]" />

                    {/* Replay the product tour */}
                    <button
                      onClick={() => { setUserMenuOpen(false); useTourStore.getState().start('replay') }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[#3e3e44] hover:text-[#0f1011] hover:bg-[rgba(15,16,17,0.04)] dark:text-[#d0d6e0] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <Compass className="h-4 w-4 text-[#8a8f98] dark:text-[#62666d]" weight="regular" />
                      <span>Take a quick tour</span>
                      <span
                        className="ml-auto pl-2 text-[#8a8f98] dark:text-[#62666d]"
                        style={{ fontSize: '11px', fontWeight: 400, letterSpacing: 0 }}
                      >
                        1 min
                      </span>
                    </button>

                    <div className="my-1 h-px bg-[rgba(15,16,17,0.06)] dark:bg-white/[0.06]" />

                    {/* Logout */}
                    <button
                      onClick={openLogoutConfirm}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[#c33a40] dark:text-[#e2545b] hover:bg-[#e2545b]/10 transition-colors duration-150"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <SignOut className="h-4 w-4" weight="regular" />
                      <span>Logout</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div
        className={cn("min-h-[100dvh] flex flex-col transition-[padding] duration-300", collapsed ? "lg:pl-[64px]" : "lg:pl-[240px]")}
        style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Top bar — transparent on dashboard (icons float over canvas), styled on other routes */}
        <header
          className={cn(
            "relative z-30 flex h-[46px] items-center justify-between px-4 sm:px-5",
            isDashboardRoute
              ? "bg-transparent"
              : "border-b border-border/30 bg-background/95 backdrop-blur-sm dark:border-white/[0.05] dark:bg-[#0f1011]/85 dark:backdrop-blur-md"
          )}
          style={editorialFontStyle}
        >
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar menu"
            >
              <List className="h-4 w-4" weight="regular" />
            </Button>
          </div>

          <div className="flex items-center space-x-1">
            {/* Theme Toggle — hidden globally per request; uncomment to restore
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                aria-label="Toggle theme"
                aria-expanded={themeMenuOpen}
                aria-haspopup="true"
                className="rounded-md dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
              >
                {actualTheme === 'dark' ? (
                  <MoonIcon className="h-4 w-4" weight="regular" />
                ) : (
                  <SunIcon className="h-4 w-4" weight="regular" />
                )}
              </Button>

              <AnimatePresence>
                {themeMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.98 }}
                    transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute right-0 top-full mt-1.5 bg-card border border-border/50 dark:bg-[#191a1b] dark:border-white/[0.08] rounded-lg shadow-lg dark:shadow-[0_8px_24px_rgba(0,0,0,0.45),0_2px_6px_rgba(0,0,0,0.3),inset_0_0_0_1px_rgba(255,255,255,0.02)] p-1 min-w-[130px] z-50"
                  >
                    {themeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setTheme(option.value as 'light' | 'dark' | 'system')
                          setThemeMenuOpen(false)
                        }}
                        className={cn(
                          'w-full flex items-center space-x-2.5 px-3 py-2 rounded-md transition-colors duration-150',
                          theme === option.value
                            ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#7170ff]/[0.10] dark:text-[#7170ff]'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04]'
                        )}
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        <option.icon className="h-4 w-4" weight="regular" />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            */}

            {/* Notifications */}
            {/* No dark: text override — the ghost variant's #d0d6e0 (Secondary tier)
                is the token for an interactive control. #8a8f98 is Tertiary, meant for
                subtitles/card descriptions, and left the bell washed out against the
                transparent top bar in dark mode (bug 21602). */}
            <div className="relative" ref={notifMenuRef}>
              <Button
                ref={notifTriggerRef}
                variant="ghost"
                size="icon"
                onClick={() => setNotifMenuOpen(!notifMenuOpen)}
                aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                aria-expanded={notifMenuOpen}
                aria-haspopup="true"
                className="relative rounded-md dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
              >
                <Bell className="h-4 w-4" weight="regular" />
                {unreadCount > 0 && (
                  <span
                    className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-[#5e6ad2] dark:bg-[#7170ff] ring-2 ring-background dark:ring-[#0f1011]"
                    aria-hidden="true"
                  />
                )}
              </Button>

              <AnimatePresence>
                {notifMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.98 }}
                    transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute right-0 top-full mt-1.5 bg-card border border-border/50 dark:bg-[#191a1b] dark:border-white/[0.08] rounded-lg shadow-lg dark:shadow-[0_8px_24px_rgba(0,0,0,0.45),0_2px_6px_rgba(0,0,0,0.3),inset_0_0_0_1px_rgba(255,255,255,0.02)] w-[340px] z-50 overflow-hidden"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 dark:border-white/[0.06]">
                      <span
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        Notifications
                      </span>
                      <button
                        onClick={() => markAllRead()}
                        disabled={unreadCount === 0}
                        className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline underline-offset-2 disabled:text-muted-foreground/50 dark:disabled:text-[#62666d] disabled:no-underline disabled:cursor-not-allowed transition-colors"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        Mark all read
                      </button>
                    </div>

                    {/* Live region — a text-only summary, deliberately NOT a
                        wrapper around the list. The list refetches on a 30s poll;
                        making the list itself the live region would re-announce
                        every row on every poll. This announces the count only. */}
                    <span className="sr-only" role="status" aria-live="polite">
                      {notifications.length === 0
                        ? 'No notifications'
                        : `${unreadCount} unread of ${notifications.length} notifications`}
                    </span>

                    <div>
                    {notifications.length === 0 ? (
                      /* Empty state */
                      <div className="flex flex-col items-center justify-center text-center px-6 py-10">
                        <div className="h-11 w-11 rounded-full bg-[#5e6ad2]/[0.08] dark:bg-[#7170ff]/[0.10] border border-[#5e6ad2]/15 dark:border-[#7170ff]/20 flex items-center justify-center mb-3.5">
                          <BellSimple className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                        </div>
                        <p
                          className="text-foreground dark:text-[#f7f8f8]"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          You're all caught up
                        </p>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] mt-1 max-w-[230px]"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                        >
                          Campaign sends, replies, and enrichment updates will show up here.
                        </p>
                      </div>
                    ) : (
                      /* List. role="menu" lives here, not on the popover shell:
                         `menu` requires menuitem children, and the live-region
                         wrapper in between would break aria-required-children. */
                      <div
                        className="max-h-[360px] overflow-y-auto divide-y divide-border/50 dark:divide-white/[0.05]"
                        role="menu"
                        aria-label="Notifications"
                      >
                        {notifications.map((n) => {
                          const Ico = notifIcon(n.type)
                          return (
                          <button
                            key={n.id}
                            type="button"
                            role="menuitem"
                            onClick={() => handleNotificationClick(n)}
                            className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-muted/40 dark:hover:bg-white/[0.04] transition-colors duration-100"
                          >
                            <span
                              className={cn(
                                'mt-0.5 h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0',
                                n.is_read
                                  ? 'bg-muted text-muted-foreground dark:bg-white/[0.05] dark:text-[#8a8f98]'
                                  : 'bg-[#5e6ad2]/[0.08] text-[#5e6ad2] dark:bg-[#7170ff]/[0.12] dark:text-[#a5b4fc]',
                              )}
                              aria-hidden="true"
                            >
                              <Ico className="h-3.5 w-3.5" weight="regular" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                <span
                                  className={cn(
                                    'block truncate flex-1',
                                    n.is_read
                                      ? 'text-muted-foreground dark:text-[#8a8f98]'
                                      : 'text-foreground dark:text-[#f7f8f8]',
                                  )}
                                  style={{ fontSize: '13px', fontWeight: n.is_read ? 410 : 510, letterSpacing: '-0.13px' }}
                                >
                                  {n.title}
                                </span>
                                {!n.is_read && (
                                  <span
                                    className="h-1.5 w-1.5 rounded-full bg-[#5e6ad2] dark:bg-[#7170ff] flex-shrink-0"
                                    aria-hidden="true"
                                  />
                                )}
                              </span>
                              {n.message && (
                                <span
                                  className="block text-muted-foreground dark:text-[#8a8f98] mt-0.5 line-clamp-2"
                                  style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.45 }}
                                >
                                  {n.message}
                                </span>
                              )}
                              {n.created_at && (
                                <span
                                  className="block text-muted-foreground/70 dark:text-[#62666d] mt-1"
                                  style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                                >
                                  {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                                </span>
                              )}
                            </span>
                          </button>
                          )
                        })}
                      </div>
                    )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Shown to every member when the plan has expired (renders nothing otherwise). */}
        <PlanLapsedBanner />

        {/* Page content. `key` on the boundary is load-bearing: the layout no
            longer remounts between routes, so without it a page that threw once
            would keep rendering its error state on every subsequent route. */}
        <main className="flex-1 px-6 sm:px-8 lg:px-10 pt-6 pb-8 bg-background">
          <ErrorBoundary key={location.pathname}>
            {children ?? <Outlet />}
          </ErrorBoundary>
        </main>
      </div>

      {/* Logout confirmation modal */}
      <ConfirmDialog
        open={logoutConfirmOpen}
        title="Log out"
        description="You'll need to sign in again to access your workspace."
        confirmLabel="Log out"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={confirmLogout}
        onCancel={() => setLogoutConfirmOpen(false)}
      />
      <UnsavedChangesPrompt />
      <ProductTour groups={visibleNavGroups} openMobileSidebar={setSidebarOpen} />
    </div>
  )
}

export default DashboardLayout
